/**
 * 退款總覽（涵蓋所有 refund_state）。
 *
 * 補上「buyer 從 PWA 發起之 requested 退票在 CMS 端無入口可見」之缺口 —
 * 既有「退票人工複核」清單只列 refund_state='manual_review'，本頁列全部狀態，
 * 為督導端全景視圖。對應 BE GET /admin/refunds。
 */

import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Button } from '../components/Button';
import { Pill } from '../components/Pill';
import { listRefunds, type RefundOverviewItem } from '../lib/api-client';

/** refund_state → 中文標籤 + Pill 色調。 */
const STATE_META: Record<
  string,
  { label: string; tone: 'neutral' | 'brand' | 'success' | 'warning' | 'danger' }
> = {
  requested: { label: '待授權', tone: 'warning' },
  refund_authorized: { label: '已授權', tone: 'brand' },
  refund_settling: { label: '金流退款中', tone: 'brand' },
  refund_settled: { label: '金流已退', tone: 'brand' },
  allowance_pending: { label: '待折讓', tone: 'brand' },
  allowance_issued: { label: '折讓完成', tone: 'brand' },
  completed: { label: '已完成', tone: 'success' },
  refund_failed: { label: '金流退款失敗', tone: 'danger' },
  allowance_failed: { label: '折讓失敗', tone: 'danger' },
  manual_review: { label: '人工複核', tone: 'danger' },
  cancelled: { label: '已取消', tone: 'neutral' },
  rejected: { label: '已拒絕', tone: 'neutral' },
};

/** 篩選下拉的狀態選項（含「全部」）。 */
const STATE_FILTERS: { value: string; label: string }[] = [
  { value: '', label: '全部狀態' },
  { value: 'requested', label: '待授權（requested）' },
  { value: 'refund_authorized', label: '已授權' },
  { value: 'refund_settling', label: '金流退款中' },
  { value: 'allowance_pending', label: '待折讓' },
  { value: 'completed', label: '已完成' },
  { value: 'manual_review', label: '人工複核' },
  { value: 'refund_failed', label: '金流退款失敗' },
  { value: 'cancelled', label: '已取消' },
  { value: 'rejected', label: '已拒絕' },
];

function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('zh-TW');
  } catch {
    return iso;
  }
}

function StateBadge({ state }: { state: string }): JSX.Element {
  const meta = STATE_META[state] ?? { label: state, tone: 'neutral' as const };
  return <Pill tone={meta.tone}>{meta.label}</Pill>;
}

export function RefundsListPage(): JSX.Element {
  const [stateFilter, setStateFilter] = useState('');

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['admin.refunds.overview', stateFilter],
    queryFn: () => listRefunds({ state: stateFilter || undefined, limit: 100 }),
  });

  const items: RefundOverviewItem[] = data?.items ?? [];

  return (
    <div>
      <div className="page-head">
        <h1 className="page-head__title">退款總覽</h1>
        <div className="page-head__actions" style={{ alignItems: 'center' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 13 }}>
            狀態
            <select
              value={stateFilter}
              onChange={(e) => setStateFilter(e.target.value)}
              style={{ padding: '4px 8px', borderRadius: 6 }}
            >
              {STATE_FILTERS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          <Button variant="secondary" onClick={() => refetch()} disabled={isFetching}>
            {isFetching ? '更新中…' : '重新整理'}
          </Button>
        </div>
      </div>

      <p className="page-intro">
        涵蓋所有退款狀態（含 buyer 由 PWA 發起、尚待授權之 requested）。人工複核項目請至「Manual
        Review（退票人工複核）」執行督導決議。
      </p>

      {isLoading && <div>載入中…</div>}
      {error && (
        <div className="error-banner">
          {(error as Error).message}
          <div style={{ marginTop: 4, fontSize: 12 }}>提醒：此頁需 admin.audit_log 權限。</div>
        </div>
      )}
      {data && (
        <>
          <div className="table-card">
            <table>
              <thead>
                <tr>
                  <th>退款 ID</th>
                  <th>訂單</th>
                  <th>金額</th>
                  <th>退款方式</th>
                  <th>票券狀態</th>
                  <th>原因</th>
                  <th>建立時間</th>
                  <th>狀態</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 && (
                  <tr>
                    <td colSpan={8} className="empty-cell">
                      目前無退款資料
                    </td>
                  </tr>
                )}
                {items.map((r) => (
                  <tr key={r.id}>
                    <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{r.id.slice(0, 8)}…</td>
                    <td style={{ fontFamily: 'monospace', fontSize: 12 }}>
                      <Link to={`/admin/orders/${r.order_id}`}>{r.order_id.slice(0, 8)}…</Link>
                    </td>
                    <td>NT$ {Number(r.refund_amount).toLocaleString()}</td>
                    <td style={{ fontSize: 12 }}>
                      {r.refund_method === 'original_payment' ? '原付款方式' : r.refund_method}
                    </td>
                    <td style={{ fontSize: 12 }}>{r.ticket_state ?? '—'}</td>
                    <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {r.reason ?? '—'}
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--c-text-muted)' }}>
                      {formatDateTime(r.created_at)}
                    </td>
                    <td>
                      <StateBadge state={r.refund_state} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="list-foot">
            <div>
              共 {data.total} 筆（第 {data.page} 頁，每頁 {data.limit}）
            </div>
          </div>
        </>
      )}
    </div>
  );
}
